import { apolloClient } from '@/setup/apollo-setup';
import { error } from '@/utils/notification';
import {
  GET_VDO_MIGRATION_DASHBOARD,
  GET_VDO_MIGRATION_TOPIC,
  ENABLE_VDO_MIGRATION,
  DISABLE_VDO_MIGRATION,
  SET_VDO_MIGRATION_CAP,
  REQUEUE_VDO_MIGRATION_JOBS,
} from '@/queries/modules/system/vdo-migration';

export async function getVdoMigrationDashboard(context, payload) {
  context.commit('setProcessing', true);
  try {
    const result = await apolloClient.query({
      query: GET_VDO_MIGRATION_DASHBOARD,
      variables: { tickLimit: payload && payload.tickLimit },
      // Never cached: this screen is refreshed precisely because an operator
      // is waiting for a number to move.
      fetchPolicy: 'network-only',
    });

    const { data, errors } = await result;

    if (errors) {
      context.commit('setError', error('notification.general-error-title', errors.map((x) => x.message).join(', ')));
      return null;
    }
    context.commit('setDashboard', data.vdoMigrationDashboard);
    context.commit('setSuccess', null);
    return data.vdoMigrationDashboard;
  } catch (e) {
    context.commit('setError', error('notification.general-error-title', e.message));
    return null;
  } finally {
    // In a finally, not on each path: the Loader overlay is bound to
    // `processing`, and one un-cleared error path leaves it spinning forever.
    context.commit('setProcessing', false);
  }
}

export async function getVdoMigrationTopic(context, payload) {
  context.commit('setProcessing', true);
  try {
    const result = await apolloClient.query({
      query: GET_VDO_MIGRATION_TOPIC,
      variables: { vdoSetTopicId: payload.vdoSetTopicId },
      fetchPolicy: 'network-only',
    });

    const { data, errors } = await result;

    if (errors) {
      context.commit('setError', error('notification.general-error-title', errors.map((x) => x.message).join(', ')));
      return null;
    }
    // A null payload is a found-nothing answer, not a failure: commit it so
    // the screen can say "no such topic in this tenant" instead of showing
    // the previous lookup's numbers next to a new id.
    context.commit('setTopic', data.vdoMigrationTopic);
    context.commit('setSuccess', null);
    return data.vdoMigrationTopic;
  } catch (e) {
    context.commit('setError', error('notification.general-error-title', e.message));
    return null;
  } finally {
    context.commit('setProcessing', false);
  }
}

/**
 * Run one mutation, mapping errors the same way the queries do.
 *
 * Written once rather than four times: four copies of this would be four
 * chances for one of them to swallow an error, and a swallowed error here is
 * a screen that says the cap changed when it did not.
 */
async function runMutation(context, mutation, variables, field) {
  context.commit('setProcessing', true);
  try {
    const result = await apolloClient.mutate({ mutation, variables });
    const { data, errors } = await result;

    if (errors) {
      context.commit('setError', error('notification.general-error-title', errors.map((x) => x.message).join(', ')));
      return null;
    }
    context.commit('setSuccess', null);
    return data[field];
  } catch (e) {
    context.commit('setError', error('notification.general-error-title', e.message));
    return null;
  } finally {
    context.commit('setProcessing', false);
  }
}

export async function enableVdoMigration(context) {
  return runMutation(context, ENABLE_VDO_MIGRATION, {}, 'enableVdoMigration');
}

export async function disableVdoMigration(context) {
  return runMutation(context, DISABLE_VDO_MIGRATION, {}, 'disableVdoMigration');
}

export async function setVdoMigrationCap(context, payload) {
  return runMutation(context, SET_VDO_MIGRATION_CAP, { cap: payload.cap }, 'setVdoMigrationCap');
}

export async function requeueVdoMigrationJobs(context, payload) {
  const result = await runMutation(
    context,
    REQUEUE_VDO_MIGRATION_JOBS,
    { reason: payload.reason || null, confirmed: Boolean(payload.confirmed) },
    'requeueVdoMigrationJobs',
  );
  if (result === null) return null;

  // Held while confirmation is outstanding, cleared the moment the requeue
  // has actually run -- a stale list beside a finished action reads as though
  // something is still pending.
  context.commit('setRequeuePreview', result.needsConfirmation ? result : null);
  // The completed counterpart: only set once the requeue has actually run
  // (never on the needsConfirmation path -- that is requeuePreview's job) and
  // only when it actually orphaned something. Echoes the reason that was
  // confirmed so the past-tense banner can say what it was for, the same way
  // the preview does.
  context.commit('setRequeueCompleted',
      !result.needsConfirmation && result.orphaned && result.orphaned.length ?
        { orphaned: result.orphaned, reason: payload.reason || null } : null);
  // `elsewhere` is undefined/null in the ordinary case (a match, or no reason
  // given) -- collapse both to null so the screen shows nothing. An empty
  // array (the reason has no jobs anywhere) and a non-empty one (the reason
  // exists, just not on FAILED) are kept distinct from that and from each
  // other.
  context.commit('setRequeueElsewhere', result.elsewhere == null ? null : result.elsewhere);
  return result;
}
